export interface IEncryptedData {
    data: string;
    salt: string;
}

export class ApiSecurity {
    async encryptData(encryptionKey: string, data: any): Promise<IEncryptedData> {
        const key = await this.generateServerDynamicKeyPBKDF2(encryptionKey, 1000);

        // Convert the key to 32-byte padded Uint8Array
        const keyData = this.padKeyToLength(key.key, 32);
        const encoder = new TextEncoder();

        // Import the key for AES-CBC encryption
        const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'AES-CBC', length: 256 }, false, [
            'encrypt',
        ]);

        // Generate a random initialization vector (IV)
        const iv = crypto.getRandomValues(new Uint8Array(16));

        // Convert the data to be encrypted into an ArrayBuffer
        const encodedData = encoder.encode(JSON.stringify(data));

        // Encrypt using AES-CBC with the key and IV
        const encryptedBuffer = await crypto.subtle.encrypt(
            {
                name: 'AES-CBC',
                iv: iv,
            },
            cryptoKey,
            encodedData
        );

        // Combine IV and encrypted data into one ArrayBuffer
        const combinedBuffer = new Uint8Array(iv.length + encryptedBuffer.byteLength);
        combinedBuffer.set(iv, 0);
        combinedBuffer.set(new Uint8Array(encryptedBuffer), iv.length);

        // Convert the combined buffer (IV + encrypted data) to Base64 string
        const encryptedValue = this.arrayBufferToBase64(combinedBuffer.buffer);
        return { data: encryptedValue, salt: key.salt };
    }

    async decryptData(
        encryptionKey: string,
        encryptedData: string,
        salt: string
    ): Promise<any> {
        // Generate the key using PBKDF2
        const key = await this.generateServerDynamicKeyPBKDF2(encryptionKey, 1000, salt);
        // this.logger.info(`Decryption key: ${key}`);

        // Convert the key to a 32-byte padded Uint8Array
        const keyData = this.padKeyToLength(key.key, 32);

        // Import the key for AES-CBC decryption
        const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'AES-CBC', length: 256 }, false, ['decrypt']);

        // Convert the Base64-encoded encrypted data back to an ArrayBuffer
        const encryptedBuffer = this.base64ToArrayBuffer(encryptedData);

        // Extract the IV (first 16 bytes) and ciphertext (remaining bytes)
        const iv = encryptedBuffer.slice(0, 16);
        const cipherText = encryptedBuffer.slice(16);

        // Decrypt the data using AES-CBC with the key and IV
        const decryptedBuffer = await crypto.subtle.decrypt(
            {
                name: 'AES-CBC',
                iv: iv,
            },
            cryptoKey,
            cipherText
        );

        // Convert decrypted ArrayBuffer back to a string and parse as JSON
        const decoder = new TextDecoder();
        return JSON.parse(decoder.decode(decryptedBuffer));
    }

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    private async generateServerDynamicKeyPBKDF2(
        sharedSecret: string,
        iterations = 1000,
        salt?: string
    ): Promise<{ key: string; salt: string }> {
        const encoder = new TextEncoder();

        // Generate a random salt if not provided
        const uniqueSalt = salt ? salt : this.generateRandomSalt();

        // Encode the shared secret and salt
        const secretKey = encoder.encode(sharedSecret);
        const saltBuffer = encoder.encode(uniqueSalt); // Corrected to use uniqueSalt

        // Import the shared secret as a key
        const cryptoKey = await crypto.subtle.importKey(
            'raw',
            secretKey,
            { name: 'PBKDF2' },
            false,
            ['deriveKey']
        );

        // Derive a key using PBKDF2
        const derivedKey = await crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: saltBuffer,
                iterations: iterations,
                hash: 'SHA-256',
            },
            cryptoKey,
            { name: 'AES-GCM', length: 256 }, // You can change the algorithm here if needed
            true, // This flag allows the key to be extractable
            ['encrypt', 'decrypt']
        );

        // Export the derived key to a base64 string
        const rawDerivedKey = await crypto.subtle.exportKey('raw', derivedKey);
        return { key: this.arrayBufferToBase64(rawDerivedKey), salt: uniqueSalt };
    }

    private generateRandomSalt(length = 16): string {
        const array = new Uint8Array(length);
        crypto.getRandomValues(array);
        return this.arrayBufferToBase64(array.buffer);
    }

    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    private padKeyToLength(key: string, length: number): Uint8Array {
        const keyBytes = new TextEncoder().encode(key);
        if (keyBytes.length > length) {
            return keyBytes.slice(0, length); // Truncate if too long
        }
        const paddedKey = new Uint8Array(length);
        paddedKey.set(keyBytes);
        return paddedKey;
    }
}